import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import {
    AdminVoucher,
    CampaignExport,
    CreateCampaignArgs,
    CreateCampaignResult,
    CreateCodeArgs,
    EditCodeArgs,
    VoucherCampaign,
    VoucherLiabilityReport,
    VoucherSalesReport,
    VoucherStatement,
    VoucherTaxCategory,
    VoucherTaxCategoryResult,
} from './voucherApiTypes';

// Vouchers & codes admin surface (Phase 1, read-only). The list endpoint
// lives on the new (ECS) backend at GET /admin/voucher; see
// new-backend/admin/src/controllers/voucherController.ts.
export const voucherApiSlice = createApi({
    reducerPath: 'voucherApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['VoucherList', 'CampaignList', 'VoucherTaxCategory'],
    endpoints: (builder) => ({
        // A prepaid voucher's full statement and provenance (#3261), behind
        // view:voucher-history. Refetched on open: refunds arrive from Stripe,
        // which nothing in this app can invalidate.
        getVoucherStatement: builder.query<VoucherStatement, string>({
            query: (voucherId) => ({ url: `/voucher/${voucherId}/statement`, method: 'GET' }),
        }),
        // Liability at the end of a day with its roll-forward (#3261).
        getVoucherLiability: builder.query<VoucherLiabilityReport, string>({
            query: (asOf) => ({ url: '/voucher/liability', method: 'GET', params: { asOf } }),
        }),
        // A year's voucher sales with their VAT (#3261), for the CSV download.
        getVoucherSales: builder.query<VoucherSalesReport, string>({
            query: (year) => ({ url: '/voucher/sales', method: 'GET', params: { year } }),
        }),
        // The VAT category prepaid vouchers are sold under (#3261 PR 6b), and
        // how many purchases still carry no VAT.
        getVoucherTaxCategory: builder.query<VoucherTaxCategory, void>({
            query: () => ({ url: '/voucher/tax-category', method: 'GET' }),
            providesTags: ['VoucherTaxCategory'],
        }),
        // Choose it; the backend also fills the purchases still without VAT.
        setVoucherTaxCategory: builder.mutation<VoucherTaxCategoryResult, string>({
            query: (taxCategoryId) => ({
                url: '/voucher/tax-category',
                method: 'PUT',
                data: { taxCategoryId },
            }),
            invalidatesTags: ['VoucherTaxCategory'],
        }),
        getVouchers: builder.query<AdminVoucher[], void>({
            query: () => ({
                url: '/voucher',
                method: 'GET',
            }),
            providesTags: ['VoucherList'],
        }),
        // Create a promo or goodwill code. Invalidates the list so the new code
        // shows immediately. 'VoucherList' is provided by getVouchers above.
        // Goodwill persistence gates on the #1000 column + BE handler (#1189);
        // the goodwill create action stays UI-gated until those land.
        createCode: builder.mutation<AdminVoucher, CreateCodeArgs>({
            query: (body) => ({
                url: '/voucher',
                method: 'POST',
                data: body,
            }),
            invalidatesTags: ['VoucherList'],
        }),
        // Edit an UNUSED promo / goodwill code (#1189). Backend rejects prepaid
        // and already-redeemed codes.
        editCode: builder.mutation<AdminVoucher, { id: string, data: EditCodeArgs }>({
            query: ({ id, data }) => ({
                url: `/voucher/${id}`,
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['VoucherList'],
        }),
        // Delete an UNUSED promo / goodwill code (#1189). Master-only; same
        // prepaid/used guards as edit.
        deleteCode: builder.mutation<void, string>({
            query: (id) => ({
                url: `/voucher/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['VoucherList'],
        }),
        // Cancel a promo / goodwill code (#1189): soft-disable — unusable but
        // kept in the DB. Allowed on used codes; prepaid rejected.
        cancelCode: builder.mutation<void, string>({
            query: (id) => ({
                url: `/voucher/${id}/cancel`,
                method: 'POST',
            }),
            invalidatesTags: ['VoucherList'],
        }),
        // Promo-code campaigns (#2249). A campaign's codes are ordinary voucher
        // rows, so every campaign write invalidates the code list as well.
        getCampaigns: builder.query<VoucherCampaign[], void>({
            query: () => ({
                url: '/voucher/campaign',
                method: 'GET',
            }),
            providesTags: ['CampaignList'],
        }),
        createCampaign: builder.mutation<CreateCampaignResult, CreateCampaignArgs>({
            query: (body) => ({
                url: '/voucher/campaign',
                method: 'POST',
                data: body,
            }),
            invalidatesTags: ['VoucherList', 'CampaignList'],
        }),
        // Cancelling also erases the recipients' addresses on the backend.
        cancelCampaign: builder.mutation<void, string>({
            query: (id) => ({
                url: `/voucher/campaign/${id}/cancel`,
                method: 'POST',
            }),
            invalidatesTags: ['VoucherList', 'CampaignList'],
        }),
        // Fetched on demand for the export; never served from cache, because
        // redemptions happen in the end-user app and nothing here invalidates.
        getCampaignExport: builder.query<CampaignExport, string>({
            query: (id) => ({
                url: `/voucher/campaign/${id}/export`,
                method: 'GET',
            }),
            keepUnusedDataFor: 0,
        }),
        // The bookable-groups query for the create-code group select moved to
        // the shared groupAdminApiSlice (getAdminGroups) — same endpoint, one
        // slice (docs/plans/deposit-admin-group-config.md).
    }),
});

export const {
    useGetVouchersQuery,
    useCreateCodeMutation,
    useEditCodeMutation,
    useDeleteCodeMutation,
    useCancelCodeMutation,
    useGetCampaignsQuery,
    useCreateCampaignMutation,
    useCancelCampaignMutation,
    useLazyGetCampaignExportQuery,
    useGetVoucherTaxCategoryQuery,
    useSetVoucherTaxCategoryMutation,
    useGetVoucherStatementQuery,
    useGetVoucherLiabilityQuery,
    useLazyGetVoucherSalesQuery,
} = voucherApiSlice;
